/**
 * 판례 전문 문장 단위 근거 추출기.
 *
 * 근거 카드가 "대충 잘라낸 스니펫"이 아니라 **판결문의 정확한 문장**을
 * 가리키도록, 전문을 문장으로 나누고 인용 조문/키워드에 해당하는 문장만
 * 골라 낸다. DOM·Node API를 쓰지 않으므로 워커에서 그대로 실행된다.
 *
 * 문장 분해 규칙:
 *   - 줄바꿈은 문장 경계다(원문의 hard wrap 포함).
 *   - `.` `?` `!` 뒤에 공백/끝이 오면 문장 경계다.
 *   - 한국어 종결 `다.` `요.`도 같은 규칙으로 끊는다.
 *   - `[1]`, `①`, `1.`, `가.`, `(1)` 같은 목록 표지는 문장에 붙여 둔다.
 *   - 날짜(`2008. 7. 4.`)와 숫자 뒤의 마침표는 문장 끝으로 보지 않는다.
 *   - 목록 표지만 있는 줄은 다음 문장 앞에 붙인다.
 */

export interface Sentence {
  /** 1-based index over the whole document, stable. */
  index: number;
  text: string;
  /** Nearest preceding section header, e.g. "이유" | "주문" | undefined. */
  section?: string;
}

export interface CitationQuery {
  /** e.g. "민법" — optional; when present, prefer sentences that also name the law. */
  lawName?: string;
  /** e.g. "제750조" or "제3조의2". */
  article?: string;
  /** e.g. "민법 제750조" — optional full label. */
  label?: string;
  max?: number; // default 3
}

/** 짧은 줄이 이 머리말로 시작하면 section으로 본다. (`판 단` 같은 띄어쓰기 변형 허용) */
const SECTION_HEADER_RE = /^(주문|판결이유|이유|청구취지|기초사실|인정사실|법리|결론|소송경과|판단)/;

/** 목록 표지만 있는 줄. 다음 문장에 붙인다. */
const MARKER_ONLY_RE = /^(?:\[[^\]]{1,8}\]|[①-⑳]|\(?\d{1,3}[).]|\(?[가-힣][).])$/;

/** 마침표 앞 토큰이 목록 표지/날짜/숫자면 문장 경계가 아니다. */
const NON_BOUNDARY_TOKEN_RE = /^(?:\d{1,4}|[가-힣]|\(\d{1,3}\)|\d{1,3}\)|\([가-힣]\)|[가-힣]\))$/;

/** 문장 끝 마침표 뒤에 붙어 있는 닫는 인용부호/괄호. */
const CLOSING_CHARS = new Set([
  "”",
  "’",
  "」",
  "』",
  "】",
  "》",
  "〉",
  "）",
  ")",
  "]",
  '"',
  "'",
]);

const DEFAULT_MAX = 3;

function limitOf(max: number | undefined): number {
  if (max === undefined || !Number.isFinite(max)) return DEFAULT_MAX;
  return Math.max(0, Math.floor(max));
}

/** 매칭용 공백 제거. 원문은 절대 변형하지 않는다. */
function compact(value: string): string {
  return value.replace(/\s+/g, "");
}

function stripLeadingMarkers(value: string): string {
  let out = value.trim();
  for (;;) {
    const next = out.replace(/^(?:\[[^\]]{1,8}\]|[①-⑳]|\(?\d{1,3}[).]|\(?[가-힣][).])\s*/, "");
    if (next === out) return out;
    out = next;
  }
}

/**
 * 줄 하나가 section 머리말이면 머리말 키워드를 돌려준다.
 * 20자 미만 + 머리말로 시작 + 문장 종결부호로 끝나지 않아야 한다.
 */
function sectionOf(line: string): string | undefined {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length >= 20) return undefined;

  let core = stripLeadingMarkers(trimmed);
  const wrapped = /^[【\[]\s*([^】\]]+?)\s*[】\]]$/.exec(core);
  if (wrapped) core = wrapped[1] ?? core;

  const flat = compact(core);
  if (!flat || /[.!?]$/.test(flat)) return undefined;

  const match = SECTION_HEADER_RE.exec(flat);
  return match ? match[1] : undefined;
}

/** `i` 위치의 마침표가 목록 표지/날짜/숫자에 속하는지. */
function isNonBoundaryDot(line: string, dot: number): boolean {
  let start = dot;
  while (start > 0 && !/\s/.test(line[start - 1] ?? "")) start--;
  return NON_BOUNDARY_TOKEN_RE.test(line.slice(start, dot));
}

/** 한 줄을 문장 조각으로 나눈다. 조각은 앞뒤 공백만 제거한다. */
function splitLine(line: string): string[] {
  const parts: string[] = [];
  let start = 0;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch !== "." && ch !== "?" && ch !== "!") continue;
    if (ch === "." && isNonBoundaryDot(line, i)) continue;

    // 문장 끝 뒤에 닫는 부호가 이어지면 함께 문장에 넣는다.
    let end = i + 1;
    while (end < line.length && CLOSING_CHARS.has(line[end] ?? "")) end++;

    // 공백/줄 끝이 아니면 문장 끝이 아니다. (예: "한다.그러나")
    if (end < line.length && !/\s/.test(line[end] ?? "")) continue;

    const part = line.slice(start, end).trim();
    if (part) parts.push(part);
    start = end;
    i = end - 1;
  }

  const tail = line.slice(start).trim();
  if (tail) parts.push(tail);
  return parts;
}

/** Split judgment text into sentences without losing list markers. */
export function segmentSentences(text: string): Sentence[] {
  const sentences: Sentence[] = [];
  let section: string | undefined;
  let pendingMarker = "";

  const push = (value: string, nextSection: string | undefined): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const sentence: Sentence = { index: sentences.length + 1, text: trimmed };
    if (nextSection) sentence.section = nextSection;
    sentences.push(sentence);
  };

  for (const rawLine of text.split(/\r\n|\n|\r/)) {
    const line = rawLine.trim();
    if (!line) continue;

    const header = sectionOf(line);
    if (header) {
      if (pendingMarker) {
        push(pendingMarker, section);
        pendingMarker = "";
      }
      section = header;
      push(line, section);
      continue;
    }

    if (MARKER_ONLY_RE.test(line)) {
      pendingMarker = pendingMarker ? `${pendingMarker} ${line}` : line;
      continue;
    }

    const parts = splitLine(line);
    if (parts.length === 0) continue;
    if (pendingMarker) {
      parts[0] = `${pendingMarker} ${parts[0] ?? ""}`;
      pendingMarker = "";
    }
    for (const part of parts) push(part, section);
  }

  if (pendingMarker) push(pendingMarker, section);
  return sentences;
}

const ARTICLE_RE = /제\s*\d+\s*조(?:\s*의\s*\d+)?/;

function extractArticle(label: string | undefined): string | undefined {
  if (!label) return undefined;
  return ARTICLE_RE.exec(label)?.[0]?.replace(/\s+/g, "");
}

function extractLaw(label: string | undefined): string | undefined {
  if (!label) return undefined;
  const match = /^(.*?)\s*제\s*\d+\s*조/.exec(label);
  const law = match?.[1]?.trim();
  return law || undefined;
}

/** Sentences that cite the article (strongest first). */
export function findCitationSentences(text: string, query: CitationQuery): Sentence[] {
  const max = limitOf(query.max);
  if (max === 0) return [];

  const article = compact(query.article ?? extractArticle(query.label) ?? "");
  if (!article) return [];

  const law = compact(query.lawName ?? extractLaw(query.label) ?? "");
  const scored: Array<{ sentence: Sentence; law: number; exact: number }> = [];
  const seen = new Set<number>();

  for (const sentence of segmentSentences(text)) {
    if (seen.has(sentence.index)) continue;
    const flat = compact(sentence.text);
    const at = flat.indexOf(article);
    if (at < 0) continue;
    seen.add(sentence.index);

    const hasLaw = law && flat.includes(law) ? 1 : 0;
    // "제750조의2"는 "제750조"의 변형으로 보고 정확 일치보다 뒤에 둔다.
    const exact = flat.slice(at + article.length).startsWith("의") ? 0 : 1;
    scored.push({ sentence, law: hasLaw, exact });
  }

  scored.sort((a, b) => b.law - a.law || b.exact - a.exact || a.sentence.index - b.sentence.index);
  return scored.slice(0, max).map((hit) => hit.sentence);
}

/** Sentences containing any of the BM25 matched terms (for keyword case results). */
export function findTermSentences(text: string, terms: string[], max = DEFAULT_MAX): Sentence[] {
  const limit = limitOf(max);
  if (limit === 0) return [];

  const needles = Array.from(
    new Set(terms.map((term) => term.trim().toLowerCase()).filter((term) => term.length > 0)),
  );
  if (needles.length === 0) return [];

  const scored: Array<{ sentence: Sentence; matches: number }> = [];
  for (const sentence of segmentSentences(text)) {
    const lower = sentence.text.toLowerCase();
    let matches = 0;
    for (const needle of needles) {
      if (lower.includes(needle)) matches++;
    }
    if (matches > 0) scored.push({ sentence, matches });
  }

  scored.sort((a, b) => b.matches - a.matches || a.sentence.index - b.sentence.index);
  return scored.slice(0, limit).map((hit) => hit.sentence);
}

/** "판결이유 제3문장" when a section is known, else "제3문장". */
export function sentenceLabel(sentence: Sentence): string {
  return sentence.section ? `${sentence.section} 제${sentence.index}문장` : `제${sentence.index}문장`;
}

export interface EvidenceLabelInput {
  kind: "citation" | "related";
  label: string;
}

/**
 * 카드/클립보드용 라벨. `related` 문장을 조문 인용처럼 보이게 하지 않는다.
 * citation: "인정사실 제34문장"
 * related:  "관련 문장 · 인정사실 제34문장" (section이 없으면 "관련 문장")
 */
export function evidenceLabel(sentence: EvidenceLabelInput): string {
  if (sentence.kind === "citation") return sentence.label;
  return sentence.label ? `관련 문장 · ${sentence.label}` : "관련 문장";
}
